import React, { useMemo, useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import mixpanel from "mixpanel-browser";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "../../../ui-components/tabs/tabs.scss";
import ContentContainer from "../../../templates/content-container/contentContainer";
import { PathParams, ShipmentDetailsPath } from "./route";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import ShipmentDetailsState from "../../../slices/shipment-details/ShipmentDetailsState";
import {
  clearDockLog,
  clearStatusHistory,
  shipmentDetailsSelector,  
  getShipmentDetails,
  resetShipmentDetails
} from "../../../slices/shipment-details/shipmentDetailsSlice";
import InvoiceDetailsInfo from "../../invoices/invoice-details/info/invoiceDetailsInfo";
import InvoiceDetailsDocuments from "../../invoices/invoice-details/documents/invoiceDetailsDocuments";
import InvoiceDetailsRecords from "../../invoices/invoice-details/records/invoiceDetailsRecords";
import InvoiceDetailsTable from "../../invoices/invoice-details/table/invoiceDetailsTable";
import FitToWindowContainer from "../../../templates/fit-to-window-container/fitToWindowContainer";
import ShipmentDetailsHeader from "./header/shipmentDetailsHeader";
import InfoBlock from "../../../templates/info-block/infoBlock";
import LabeledValue from "../../../ui-components/molecules/labeled-value/labeledValue";
import InfoGrid from "../../../templates/info-grid/infoGrid";
import {
  ContentItemTitle,
  ContentItem
} from "../../../templates/content-item/contentItem";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import SteppedProgress from "../../../ui-components/stepped-progress/steppedProgress";
import BlockPlaceholder from "../../../ui-components/block-placeholder/blockPlaceholder";
import InvoicePayment from "../../invoices/invoice-payment/invoicePayment";
import ShipmentClaimsNumbers from "./shipmentClaimsNumbers";
import ShipmentCommentsList from "./shipmentCommentsList";
import ShipmentAddComment from "./shipmentAddComment";
import ShipmentDockLog from "./shipmentDockLog";
import ShipmentStatusHistory from "./shipmentStatusHistory";
import ShipmentClaimsDocuments from "./shipmentClaimsDocuments";
import RoutingGuide from "../../../app/data/tracking/RoutingGuide";
import { userSelector } from "../../../slices/user/userSlice";
import UserState from "../../../slices/user/UserState";
import { UserUtils } from "../../../app/data/user/userUtils";
import ShipmentExceptions from "./shipmentExceptions";
import ShipmentTrackingHistory from "./shipmentTrackingHistory";
import { CreateException } from "../../exceptions/createException";
import { getExceptions, resetExceptionState } from "../../../slices/exceptions/exceptionsSlice";
import { SHIPMENT_STATUSES } from "../../../app/data/common/ShipmentStatuses";
import ShipmentDetailsModel from "../../../app/data/tracking/ShipmentDetailsModel";
import TrailerLocation from "../../../features/trailer-location/trailerLocation";
import plusBlue from "../../../images/plus__blue.svg";
import Banner, { BannerColor } from "../../../ui-components/banner/banner";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import { Routes } from "../../../app/route/RoutesConfig";
import useQuery from "../../../hooks/useQuery";
import { CreateAppointment } from "../../appointment/createAppointment";
import "./shipmentDetails.scss";

const ShipmentDetails: React.FC<{}> = (props) => {
  const userState: UserState = useSelector(userSelector);
  const shipmentDetailsState: ShipmentDetailsState = useSelector(shipmentDetailsSelector);
  const [showAddComment, setShowAddComment] = useState(false);
  const [showTrackingHistorySidebar, setShowTrackingHistorySidebar] = useState(false);
  const [showCreateAppointment, setShowCreateAppointment] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedClaimsTabIndex, setSelectedClaimsTabIndex] = useState(0);
  const dispatch = useDispatch();
  const params = useParams() as PathParams;
  const history = useHistory();
  const location = useLocation();
  const tabsRef = useRef<HTMLDivElement>(null);
  const claimsTabsRef = useRef<HTMLDivElement>(null);
  const invoiceNumber = useMemo(() => {
    return parseInt(params[ShipmentDetailsPath.invoiceNumber], 10);
  }, [params]);
  const pickupId = useQuery().get("pickupId");

  const showDay = (value: string) => {
    return value || "_";
  };

  const getRoutingGuideString = (guide: RoutingGuide) => {
    return (
      showDay(guide.monday) +
      " " +
      showDay(guide.tuesday) +
      " " +
      showDay(guide.wednesday) +
      " " +
      showDay(guide.thursday) +
      " " +
      showDay(guide.friday) +
      " " +
      showDay(guide.saturday) +
      " " +
      showDay(guide.sunday)
    );
  };

  const isProgressLastActiveStep = (shipment: ShipmentDetailsModel, i: number) => {
    if (!shipment.progress[i].reached) return false;
    if (shipment.progress[i + 1] && !shipment.progress[i + 1].reached) return true;
    return false;
  };

  const generateProgressSteps = (shipment: ShipmentDetailsModel) => {
    if (!shipment.progress) return [];
    const steps = shipment.progress.map((step, index) => {
      let newObj = {
        title: step.statusTitle,
        completed: step.completed,
        reached: step.reached,
        details: <></>
      };
      if (shipment.status?.description === SHIPMENT_STATUSES.outForDelivery &&
          shipment.trailer &&
          shipment.showOnMap &&
          (step.statusTitle === "Out for Delivery" || isProgressLastActiveStep(shipment, index))) {
        newObj.details = <TrailerLocation invoiceNumber={shipment.invoice.invoiceNumber} textMode={true} />;
      }
      return newObj;
    });

    return shipment.pickup ? [...steps, { title: "", completed: false, reached: false }] : steps;
  };

  useEffect(() => {
    if (invoiceNumber || shipmentDetailsState.needToReload) {
      dispatch(getShipmentDetails(invoiceNumber, pickupId || undefined));
      UserUtils.isEmployee(userState.profile) && dispatch(getExceptions(invoiceNumber));
      return function cleanup() {
        dispatch(resetShipmentDetails());
        dispatch(clearDockLog());
        dispatch(clearStatusHistory());
        dispatch(resetExceptionState());
      };
    }
  }, [dispatch, invoiceNumber, shipmentDetailsState.needToReload, userState.profile, pickupId]);

  useEffect(() => {
    if (UserUtils.isXGSUser(userState.profile) || UserUtils.isXGSAdministrator(userState.profile)) return;
    mixpanel.track("Viewed a probill", { "Probill": invoiceNumber });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (shipmentDetailsState.shipment && shipmentDetailsState.shipment.invoice.invoiceNumber !== invoiceNumber) {
      history.push(`${Routes.shipments.tracking}/${shipmentDetailsState.shipment.invoice.invoiceNumber}`);
    }
  }, [history, invoiceNumber, shipmentDetailsState.shipment]);

  // Handle query parameters for navigation from notifications
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    const scrollTo = searchParams.get('scrollTo');
    const source = searchParams.get('source');

    // Only handle if coming from notification and shipment is loaded
    if (source === 'notification' && shipmentDetailsState.loaded && shipmentDetailsState.shipment) {
      if (tab === 'exceptions') {
        setSelectedTabIndex(3);
        
        if (scrollTo === 'tabs' && tabsRef.current) {
          setTimeout(() => {
            tabsRef.current?.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }, 100);
        }
      } else if (tab === 'claims') {
        const claimsTabIndex = 2;
        setSelectedClaimsTabIndex(claimsTabIndex);
        
        if (scrollTo === 'tabs' && claimsTabsRef.current) {
          setTimeout(() => {
            claimsTabsRef.current?.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }, 100);
        }
      }
    }
  }, [location.search, shipmentDetailsState.loaded, shipmentDetailsState.shipment, userState.profile]);

  return (
    <>
      <ContentContainer titleComponent={<ShipmentDetailsHeader onClickTrackingHistory={() => setShowTrackingHistorySidebar(true)} />}>
        <FitToWindowContainer>
          {shipmentDetailsState.loadingFailed && (
            <XGSErrorMessage>{shipmentDetailsState.error}</XGSErrorMessage>
          )}
          {shipmentDetailsState.loaded && (
            <div className="xgs-shipment-details">
              <div className="xgs-shipment-details__main-container">
                {shipmentDetailsState?.shipment?.progress && (
                  <div style={{
                    maxWidth: 896,
                    marginBottom: 36
                  }}>
                    <SteppedProgress
                      steps={generateProgressSteps(shipmentDetailsState?.shipment)}
                    />
                  </div>
                )}
                {/* Show appointment required banner for non-completed shipments that require appointments */}
                {UserUtils.isEmployee(userState.profile) &&
                 shipmentDetailsState.shipment?.appointmentRequired && 
                 !shipmentDetailsState.shipment?.appointment &&
                 shipmentDetailsState.shipment?.status?.description !== SHIPMENT_STATUSES.delivered &&
                 shipmentDetailsState.shipment?.status?.description !== SHIPMENT_STATUSES.warehoused && (
                  <Banner mods={{ color: BannerColor.red }} mix="xgs-shipment-details__appointment-banner">
                    <div className="xgs-shipment-details__appointment-banner__content">
                      This shipment requires an appointment for delivery. Please schedule an appointment to ensure timely delivery.
                    </div>

                    <Button
                      theme={ButtonThemes.blue}
                      className="xgs-banner__action-button"
                      onClick={() => setShowCreateAppointment(true)}
                    >
                      Schedule Appt.
                    </Button>
                  </Banner>
                )}
                {shipmentDetailsState.shipment && (
                  <>
                    {shipmentDetailsState.shipment.claims && (
                      <ShipmentClaimsNumbers claims={shipmentDetailsState.shipment.claims} />
                    )}
                    {shipmentDetailsState.shipment?.status?.title !== "BOOKED" && (
                      <InfoGrid className="xgs-shipment-details__grid">
                        <InfoBlock title="ETA">
                          {(shipmentDetailsState?.shipment?.eta?.originalEta || shipmentDetailsState?.shipment?.eta?.expectedEta) && (
                            <>
                              <LabeledValue
                                label="Original ETA:"
                                value={shipmentDetailsState?.shipment?.eta?.originalEta?.toUiDateFormat()}
                                labelFixWidth
                              />
                              <LabeledValue
                                label="Expected ETA:"
                                value={shipmentDetailsState?.shipment?.eta?.expectedEta?.toUiDateFormat()}
                                labelFixWidth
                              />
                              {shipmentDetailsState?.shipment?.eta?.notes && shipmentDetailsState?.shipment?.eta?.notes.length > 0 && (
                                <div className="xgs-form__field__notes">
                                  <strong>Note{shipmentDetailsState.shipment.eta.notes.length > 1 ? "s" : ""}:</strong>&nbsp;
                                  {shipmentDetailsState.shipment.eta.notes.length === 1 && (
                                    <>
                                      {shipmentDetailsState.shipment.eta.notes[0]}
                                    </>
                                  )}
                                  {shipmentDetailsState.shipment.eta.notes.length > 1 && (
                                    <ul>
                                      {shipmentDetailsState.shipment.eta.notes.map(note => (
                                        <li>
                                          {note}
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                          {shipmentDetailsState?.shipment?.routingGuide && (
                            <LabeledValue
                              label="Routing guide"
                              value={getRoutingGuideString(
                                  shipmentDetailsState.shipment.routingGuide
                              )}
                              labelFixWidth
                            />
                          )}
                          {!shipmentDetailsState?.shipment?.eta?.originalEta &&
                            !shipmentDetailsState?.shipment?.eta?.expectedEta &&
                            !shipmentDetailsState?.shipment?.routingGuide &&
                            !shipmentDetailsState?.shipment?.eta?.notes && (
                            <BlockPlaceholder />
                          )}
                        </InfoBlock>
                        <InfoBlock title="POD" className="xgs-shipment-details__grid__double-column">
                          <div className={`
                            xgs-shipment-details__grid__double-column__grid
                            ${!shipmentDetailsState?.shipment?.pod ? " xgs-shipment-details__grid__double-column__grid--empty" : ""}
                          `}>
                            {shipmentDetailsState?.shipment?.pod && (
                              <>
                                <div style={{ marginRight: 24 }}>
                                  <LabeledValue
                                    label="POD:"
                                    value={shipmentDetailsState.shipment.pod.date ? `${shipmentDetailsState.shipment.pod.date.toUiDateFormat()} ${shipmentDetailsState.shipment.pod.time ? shipmentDetailsState.shipment.pod.time : ''}` : ''}
                                    labelFixWidth
                                  />
                                  <LabeledValue
                                    label="By:"
                                    value={shipmentDetailsState.shipment.pod.signature || ''}
                                    labelFixWidth
                                  />
                                </div>
                                {shipmentDetailsState.shipment.fullAccess && (
                                  <div>
                                    <LabeledValue
                                      label="POD Entered:"
                                      value={shipmentDetailsState.shipment.pod.enteredDate ? `${shipmentDetailsState.shipment.pod.enteredDate.toUiDateFormat()} ${shipmentDetailsState.shipment.pod.enteredTime ? shipmentDetailsState.shipment.pod.enteredTime : ''}` : ''}
                                      labelFixWidth
                                    />
                                    <LabeledValue
                                      label="By:"
                                      value={shipmentDetailsState.shipment.pod.signature || ''}
                                      labelFixWidth
                                    />
                                  </div>
                                )}
                              </>
                            )}
                            {!shipmentDetailsState?.shipment?.pod && <BlockPlaceholder />}
                          </div>
                        </InfoBlock>
                      </InfoGrid>
                    )}
                    <InvoiceDetailsInfo {...shipmentDetailsState?.shipment} />
                  </>
                )}
                {shipmentDetailsState.shipment?.fullAccess && (shipmentDetailsState.shipment?.status?.title !== "BOOKED") && (
                  <InvoiceDetailsTable
                    items={shipmentDetailsState.shipment?.items}
                    invoiceAmount={shipmentDetailsState.shipment?.freightCharges}
                  />
                )}

                <ContentItem className="xgs-shipment-details__assets">
                  <div ref={claimsTabsRef} className={`xgs-shipment-details__tabs${!(UserUtils.isXGSUser(userState.profile) || UserUtils.isXGSAdministrator(userState.profile)) && !shipmentDetailsState.shipment?.claims ? " xgs-shipment-details__tabs--single" : ""}`}>
                    <Tabs selectedIndex={selectedClaimsTabIndex} onSelect={setSelectedClaimsTabIndex}>
                      <TabList>
                        <Tab>Invoice</Tab>
                        {(UserUtils.isXGSUser(userState.profile) || UserUtils.isXGSAdministrator(userState.profile)) && invoiceNumber && (
                          <Tab>Images</Tab>
                        )}
                        {shipmentDetailsState.shipment?.claims && (
                          <Tab>Claims</Tab>
                        )}
                      </TabList>
                      <TabPanel>
                        {invoiceNumber && <InvoiceDetailsDocuments invoiceNumber={invoiceNumber} />}
                      </TabPanel>
                      {(UserUtils.isXGSUser(userState.profile) || UserUtils.isXGSAdministrator(userState.profile)) && invoiceNumber && (
                        <TabPanel>
                          <InvoiceDetailsRecords invoiceNumber={invoiceNumber} />
                        </TabPanel>
                      )}
                      {shipmentDetailsState.shipment?.claims && (
                        <TabPanel>
                          <ShipmentClaimsDocuments invoiceNumber={invoiceNumber} />
                        </TabPanel>
                      )}
                    </Tabs>
                  </div>
                </ContentItem>

                {shipmentDetailsState.shipment?.fullAccess && (UserUtils.isXGSUser(userState.profile) || UserUtils.isXGSAdministrator(userState.profile)) && (
                  <div ref={tabsRef} className="xgs-shipment-details__tabs xgs-shipment-details__shipment-tabs">
                    <Tabs selectedIndex={selectedTabIndex} onSelect={setSelectedTabIndex}>
                      <TabList>
                        <Tab>Dock Log</Tab>
                        <Tab>Comments</Tab>
                        <Tab>Status History</Tab>
                        <Tab>Exceptions</Tab>
                      </TabList>
                      <TabPanel>
                        <ShipmentDockLog invoiceNumber={invoiceNumber} />
                      </TabPanel>
                      <TabPanel>
                        {!shipmentDetailsState.shipment?.notes && (
                          <div className="xgs-shipment-details__tabs__no-data">There are no comments</div>
                        )}
                        <div style={{ backgroundColor: "white" }}>
                          {shipmentDetailsState.shipment?.notes && <ShipmentCommentsList comments={shipmentDetailsState.shipment?.notes} />}
                          <Button
                            theme={ButtonThemes.blueInverted}
                            className="xgs-shipment-details__add-comment-button"
                            onClick={() => setShowAddComment(true)}
                          >
                            <img src={plusBlue} alt="Plus" /> Add New Comment
                          </Button>
                        </div>
                        <ShipmentAddComment
                          invoiceNumber={invoiceNumber}
                          show={showAddComment}
                          onClose={() => setShowAddComment(false)}
                        />
                      </TabPanel>
                      <TabPanel>
                        <ShipmentStatusHistory invoiceNumber={invoiceNumber} />
                      </TabPanel>
                      <TabPanel>
                        <ShipmentExceptions probillNumber={invoiceNumber}/>
                      </TabPanel>
                    </Tabs>
                  </div>
                )}
                {shipmentDetailsState.shipment?.fullAccess && shipmentDetailsState.shipment?.notes && !(UserUtils.isXGSUser(userState.profile) || UserUtils.isXGSAdministrator(userState.profile)) && (
                  <ContentItem>
                    <ContentItemTitle>Comments</ContentItemTitle>
                    {shipmentDetailsState.shipment?.notes && <ShipmentCommentsList comments={shipmentDetailsState.shipment?.notes} />}
                  </ContentItem>
                )}
              </div>
              {shipmentDetailsState.shipment?.fullAccess && (
                <div className="xgs-shipment-details__tracking-history-container xgs-shipment-details__tracking-history-container--wide-screen">
                  <ShipmentTrackingHistory probillNumber={invoiceNumber} />
                </div>
              )}
            </div>
          )}
        </FitToWindowContainer>
      </ContentContainer>
      {shipmentDetailsState.shipment?.fullAccess && (
        <div className="xgs-shipment-details__tracking-history-container xgs-shipment-details__tracking-history-container--narrow-screen">
          <SlideOutSidebar
            header="Tracking History"
            onClose={() => setShowTrackingHistorySidebar(false)}
            show={showTrackingHistorySidebar}
            spinner={false}
            size="small"
          >
            <ShipmentTrackingHistory probillNumber={invoiceNumber} inSidebar />
          </SlideOutSidebar>
        </div>
      )}
      <InvoicePayment invoiceNumber={invoiceNumber?.toString() || ''} />
      {UserUtils.isEmployee(userState.profile) && <CreateException probillNumber={invoiceNumber}/>}
      {showCreateAppointment && (
        <CreateAppointment
          probillNumber={invoiceNumber}
          onClose={() => {
            setShowCreateAppointment(false)
            dispatch(getShipmentDetails(invoiceNumber));
          }}
        />
      )}
    </>
  );
};

export default ShipmentDetails;
